"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SurveyObjectBox = void 0;
/*
 * Copyright Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
const react_1 = __importDefault(require("react"));
const ObjectReviewControls_1 = __importDefault(require("../validations/ObjectReviewControls"));
const reviewDecisionStatusHelper_1 = require("../../../services/admin/reviewDecisionStatusHelper");
const useObjectReview_1 = require("../../../services/admin/useObjectReview");
const selectableKeyboard_1 = require("../../../services/admin/selectableKeyboard");
/**
 * Renders a reviewable survey object box with status styling and approve/reject controls.
 * Review status is read from the `reviewDecisions` Redux slice; the controls dispatch
 * the review thunks themselves (see `ObjectReviewControls`).
 * @param props - Object identity, layout, and child content
 * @returns A `div` or `details` element with review UI wired consistently
 */
const SurveyObjectBox = (props) => {
    var _a;
    const { objectType, objectUuid, extraClassNames = '', children, inheritedStatus, nested = false } = props;
    const [uncontrolledOpen, setUncontrolledOpen] = react_1.default.useState(props.as === 'details' ? ((_a = props.defaultOpen) !== null && _a !== void 0 ? _a : false) : false);
    const isControlledDetails = props.as === 'details' && props.open !== undefined;
    const detailsOpen = isControlledDetails ? props.open : uncontrolledOpen;
    const review = (0, useObjectReview_1.useObjectReview)(objectType, objectUuid);
    // The panels pass the parent's decision down. A rejected parent hides approve; the server still accepts it.
    const approvalBlocked = inheritedStatus === 'rejected';
    const className = (0, reviewDecisionStatusHelper_1.buildSurveyObjectBoxClassName)({
        objectType,
        status: review.status,
        extraClassNames,
        objectUuid,
        inheritedStatus,
        hasReviewControls: review.hasReviewControls,
        nested
    });
    const reviewButtons = review.hasReviewControls ? (react_1.default.createElement(ObjectReviewControls_1.default, { review: review, objectType: objectType, objectUuid: objectUuid, approvalBlocked: approvalBlocked })) : null;
    if (props.as === 'details') {
        const summaryElement = react_1.default.isValidElement(props.summary) && props.summary.type === 'summary' ? (props.summary) : (react_1.default.createElement("summary", null, props.summary));
        const handleDetailsToggle = (event) => {
            var _a;
            const nextOpen = event.currentTarget.open;
            if (!isControlledDetails) {
                setUncontrolledOpen(nextOpen);
            }
            (_a = props.onToggle) === null || _a === void 0 ? void 0 : _a.call(props, nextOpen);
        };
        // Review buttons are rendered as a sibling of the summary (not inside it) so
        // interactive controls are not nested in the disclosure button (accessibility).
        return (react_1.default.createElement("details", { open: detailsOpen, onToggle: handleDetailsToggle, className: className },
            summaryElement,
            reviewButtons,
            children));
    }
    const { onClick, selectableClassName = '', isActive = false, selectableContent } = props;
    const hasSelectableContent = selectableContent !== null && selectableContent !== undefined;
    const selectableInner = hasSelectableContent ? selectableContent : children;
    const trailingContent = hasSelectableContent ? children : null;
    const selectableControlClassName = [selectableClassName, isActive ? '_active' : ''].filter(Boolean).join(' ');
    const handleSelectableClick = (event) => {
        onClick === null || onClick === void 0 ? void 0 : onClick();
        event.stopPropagation();
    };
    return (react_1.default.createElement("div", { className: className },
        reviewButtons,
        onClick ? (react_1.default.createElement(react_1.default.Fragment, null,
            react_1.default.createElement("div", { role: "button", tabIndex: 0, className: selectableControlClassName || undefined, "aria-pressed": isActive, onClick: handleSelectableClick, onKeyDown: (0, selectableKeyboard_1.createKeyboardActivateHandler)(() => onClick(), { stopPropagation: true }) }, selectableInner),
            trailingContent)) : (children)));
};
exports.SurveyObjectBox = SurveyObjectBox;
//# sourceMappingURL=SurveyObjectBox.js.map